import { Helmet } from "react-helmet-async";

const SITE_NAME = "Servello";
const DEFAULT_DESC = "Staffing made easy for hospitality and events. Connect with vetted catering, bartending, and event staff. Post shifts, get matched, manage everything — all in one place.";
const DEFAULT_IMAGE = "https://hireservello.com/og-image.png";
const SITE_URL = "https://hireservello.com";

export function SEOHead({
  title,
  description = DEFAULT_DESC,
  path = "",
  type = "website",
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Staffing Made Easy for Hospitality & Events`;
  const canonicalUrl = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="keywords" content="staffing, hospitality, catering, events, bartender, server, temp staff, hire staff, find work, event staffing, restaurant staffing" />
      <meta name="author" content="Servello by Tylegn" />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-built JSON-LD schemas
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Servello",
  url: SITE_URL,
  logo: DEFAULT_IMAGE,
  description: DEFAULT_DESC,
  telephone: "+18556673783",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-855-66-SERVELLO",
    contactType: "customer service",
    availableLanguage: "English",
  },
  sameAs: [],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Servello",
  url: SITE_URL,
  description: DEFAULT_DESC,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/directory?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function buildJobPostingSchema(shift) {
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: shift.title || "Hospitality Staff",
    description: shift.description || `Staffing opportunity for ${shift.title}`,
    datePosted: shift.created_at || new Date().toISOString(),
    employmentType: "TEMPORARY",
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        streetAddress: shift.location || "",
      },
    },
    hiringOrganization: {
      "@type": "Organization",
      name: shift.business_name || "Servello Business",
      sameAs: SITE_URL,
    },
    baseSalary: shift.roles?.[0] ? {
      "@type": "MonetaryAmount",
      currency: "USD",
      value: {
        "@type": "QuantitativeValue",
        value: shift.roles[0].pay_rate,
        unitText: "HOUR",
      },
    } : undefined,
  };
}

export function buildLocalBusinessSchema(business) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: business.business_name || business.name,
    description: business.ai_description || business.bio || `${business.business_name} — hospitality business on Servello`,
    url: `${SITE_URL}/directory?q=${encodeURIComponent(business.business_name || "")}`,
    telephone: "+1-855-66-SERVELLO",
    address: business.location ? {
      "@type": "PostalAddress",
      streetAddress: business.location,
    } : undefined,
    aggregateRating: business.avg_rating ? {
      "@type": "AggregateRating",
      ratingValue: business.avg_rating,
      bestRating: 5,
      ratingCount: business.rating_count || 1,
    } : undefined,
  };
}
